import { useState } from "react";
import { Wallet, Info, Sparkles, TrendingUp, Package, MessageCircle, ArrowUpRight, Loader2, ChevronDown } from "lucide-react";
import { formatTRY } from "../lib/modelAnalysis";
import { resolveSite } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import { pub } from "../lib/pubThemeClasses";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { trackQuoteCTAClick, trackWhatsappClick } from "../lib/tracking";

function resolveBadge(t, backendEstimate, fallbackReason, loading) {
  const ps = backendEstimate?.pricing_status;
  if (ps === "pending_review" || fallbackReason === "pending-review") {
    return { label: t("price.badge.pending"), tone: "text-violet-800 border-violet-300/60 bg-violet-100/70 dark:text-violet-200 dark:border-violet-400/35 dark:bg-violet-500/10" };
  }

  const pricing = backendEstimate?.pricing;
  const conf = pricing?.confidence;
  if (conf === "high") {
    return { label: t("price.level.high"), tone: "text-[#008f5a] border-[#00FF9D]/50 bg-[#00FF9D]/12 dark:text-[#00FF9D] dark:border-[#00FF9D]/40 dark:bg-[#00FF9D]/5" };
  }
  if (conf === "medium") {
    return { label: t("price.level.medium"), tone: "text-sky-700 border-sky-300/60 bg-sky-500/10 dark:text-[#00E5FF] dark:border-[#00E5FF]/40 dark:bg-[#00E5FF]/5" };
  }
  if (conf === "low") {
    return { label: t("price.level.low"), tone: "text-amber-800 border-amber-300/60 bg-amber-100/80 dark:text-amber-300 dark:border-amber-400/40 dark:bg-amber-400/5" };
  }

  if (loading) {
    return {
      label: t("price.badge.analyzingLabel"),
      tone: `text-neutral-600 bg-neutral-100/80 dark:text-zinc-400 dark:bg-white/5 border ${pub.borderStrongTone}`,
    };
  }

  return { label: t("price.level.low"), tone: "text-amber-800 border-amber-300/60 bg-amber-100/80 dark:text-amber-300 dark:border-amber-400/40 dark:bg-amber-400/5" };
}

function fallbackMessage(t, key) {
  const map = {
    "no-file": "price.fallback.noFile",
    analyzing: "price.fallback.analyzing",
    "pending-review": "price.fallback.pendingReview",
    "technical-review": "price.fallback.technicalReview",
  };
  return t(map[key] || "price.fallback.technicalReview");
}

/** @typedef {{ title: string, body?: string | null }} PendingCopy */
/** @returns {PendingCopy | null} */
function resolvePendingReviewHintCopy(t, hint, quoteFileExt) {
  if (hint === "too_many_triangles") {
    return { title: t("price.review.tooDetailed") };
  }
  if (hint === "analyze_timeout") {
    return { title: t("price.review.timeout") };
  }
  if (quoteFileExt === ".3mf" && hint === "3mf_geometry") {
    return { title: t("price.review.generic3mf"), body: t("price.3mf.reviewCardBody") };
  }
  return null;
}

const PRICE_DEBUG_ON = typeof process.env.REACT_APP_PRICE_DEBUG !== "undefined" && process.env.REACT_APP_PRICE_DEBUG === "1";

/** @param {{ backendEstimate?: object | null; loading?: boolean; fallbackReason?: string; ctaScrollTargetId?: string; detected3mfSettings?: object | null; userOverrode3mfSettings?: boolean; quoteFileExt?: string | null }} props */
export default function PriceEstimateCard({
  backendEstimate,
  loading,
  fallbackReason,
  ctaScrollTargetId = "quote-contact-form",
  detected3mfSettings = null,
  userOverrode3mfSettings = false,
  quoteFileExt = null,
}) {
  const { settings } = usePublicData();
  const site = resolveSite(settings);
  const { t, language } = useLanguage();
  const pricing = backendEstimate?.pricing || null;
  const geometry = backendEstimate?.geometry || null;
  const pricingReady =
    pricing != null && typeof pricing.low === "number" && typeof pricing.high === "number";
  const mass = backendEstimate?.mass_estimate_g || null;
  const time = backendEstimate?.print_time_estimate_h || null;
  const supportRisk = backendEstimate?.support_risk || null;
  const printSettings = backendEstimate?.customer_print_settings || null;
  const badge = resolveBadge(t, backendEstimate, fallbackReason, loading);
  const isReady = pricingReady;
  const isPendingReview =
    !loading &&
    !pricingReady &&
    (fallbackReason === "pending-review" || backendEstimate?.pricing_status === "pending_review");

  const hintCopy = isPendingReview ? resolvePendingReviewHintCopy(t, backendEstimate?.pending_review_hint, quoteFileExt) : null;
  /** Eski kayıtlar: hint yok, 3MF ve pending → eski kart kopyası */
  const legacy3mfPendingCard =
    isPendingReview && quoteFileExt === ".3mf" && !hintCopy && !backendEstimate?.pending_review_hint;
  /** Diğer: sunucunun İngilizce message alanı veya genel fallback */
  const fallbackBackendMessage =
    isPendingReview && !hintCopy && !legacy3mfPendingCard ? (backendEstimate?.message && String(backendEstimate.message).trim()) : "";

  const [calcOpen, setCalcOpen] = useState(false);

  const scrollToForm = () => {
    if (!ctaScrollTargetId) return;
    document.getElementById(ctaScrollTargetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fmtPv = fmtPrintLabel;
  const supportLabel = supportRisk === "low" ? t("price.support.low") : supportRisk === "medium" ? t("price.support.medium") : supportRisk === "high" ? t("price.support.high") : supportRisk;
  const breakdown = pricing?.breakdown || null;

  return (
    <div data-testid="price-estimate-card" className={`border relative overflow-hidden rounded-sm ${pub.cardElevated}`}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF9D]/40 dark:via-[#00FF9D] to-transparent opacity-70" />

      <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b bg-white dark:bg-transparent ${pub.borderSubtle}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#00FF9D]/40 bg-[#00FF9D]/15 dark:bg-[#00FF9D]/10 grid place-items-center">
            <Wallet className="w-4 h-4 text-[#009966] dark:text-[#00FF9D]" strokeWidth={1.8} />
          </div>
          <div>
            <div className={`font-sans font-semibold text-base ${pub.heroHeading}`}>{t("price.title")}</div>
            <div className={`font-sans text-xs mt-1 ${pub.mutedStr}`}>{t("price.subtitle")}</div>
          </div>
        </div>
        <span className={`font-mono text-xs leading-snug px-2 py-1 border shrink-0 text-right ${badge.tone}`} data-testid="price-confidence-badge">
          {badge.label}
        </span>
      </div>

      {!isReady ? (
        <div className="px-5 py-8 text-center bg-white dark:bg-[#080808]">
          {loading ? (
            <>
              <Loader2 className={`w-6 h-6 mx-auto mb-4 animate-spin stroke-[1.5] ${pub.iconMuted}`} />
              <p className={`text-sm font-medium mb-1.5 ${pub.heroHeading}`} data-testid="price-fallback-message">
                {t("loading.title")}
              </p>
              <p className={`text-xs ${pub.muted}`}>{t("loading.subtitle")}</p>
            </>
          ) : (
            <>
              <Sparkles className={`w-6 h-6 mx-auto mb-4 stroke-[1.5] ${pub.iconMuted}`} />
              {hintCopy ? (
                <div className="max-w-sm mx-auto space-y-2" data-testid="price-fallback-message">
                  <p className={`text-sm font-medium leading-relaxed ${pub.heroHeading}`}>{hintCopy.title}</p>
                  {hintCopy.body ? (
                    <p className={`text-xs leading-relaxed ${pub.muted}`}>{hintCopy.body}</p>
                  ) : null}
                </div>
              ) : legacy3mfPendingCard ? (
                <div className="max-w-sm mx-auto space-y-2" data-testid="price-fallback-message">
                  <p className={`text-sm font-medium leading-relaxed ${pub.heroHeading}`}>{t("price.3mf.reviewCardTitle")}</p>
                  <p className={`text-xs leading-relaxed ${pub.muted}`}>{t("price.3mf.reviewCardBody")}</p>
                </div>
              ) : fallbackBackendMessage ? (
                <p className={`text-sm leading-relaxed max-w-sm mx-auto ${pub.heroHeading}`} data-testid="price-fallback-message">
                  {fallbackBackendMessage}
                </p>
              ) : (
                <p className={`text-sm leading-relaxed max-w-sm mx-auto ${pub.muted}`} data-testid="price-fallback-message">
                  {fallbackMessage(t, fallbackReason)}
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className={`px-5 py-8 md:py-10 text-center border-b bg-white dark:bg-[#09090b] ${pub.borderSubtle}`}>
            <div className={`font-sans text-sm font-medium mb-3 ${pub.muted}`}>{t("price.range.label")}</div>
            <div
              className={`font-sans text-2xl sm:text-3xl xl:text-4xl font-extrabold tabular-nums tracking-tight leading-tight ${pub.heroHeading}`}
              data-testid="price-range-value"
            >
              <span>{formatTRY(pricing.low)}</span>
              <span className="text-[#FF4F00] mx-2 md:mx-3">—</span>
              <span>{formatTRY(pricing.high)}</span>
            </div>
            <div className={`mt-4 font-mono text-xs flex flex-wrap items-center justify-center gap-x-4 gap-y-1 uppercase tracking-wide ${pub.muted}`}>
              {pricingReady && geometry?.volume_cm3 != null && geometry.volume_cm3 !== "" && Number(geometry.volume_cm3) > 0 && (
                <span className={`inline-flex items-center gap-1.5 ${pub.iconMuted}`}>
                  <Package className="w-3 h-3" /> {Number(geometry.volume_cm3).toFixed(2)} cm³
                </span>
              )}
              {supportRisk ? (
                <span className={`inline-flex items-center gap-1.5 ${pub.iconMuted}`}>
                  <TrendingUp className="w-3 h-3" /> {t("price.support.prefix")} {supportLabel}
                </span>
              ) : null}
            </div>
            {PRICE_DEBUG_ON && geometry?.volume_cm3 != null && geometry.volume_cm3 !== "" ? (
              <p className={`mt-3 font-mono text-[10px] text-center uppercase tracking-wide ${pub.muted}`} data-testid="price-debug-volume">
                Debug — Volume: {Number(geometry.volume_cm3).toFixed(2)} cm³
                {breakdown?.raw_total_low != null ? ` · raw_low: ${Number(breakdown.raw_total_low).toFixed(2)}` : ""}
                {breakdown?.min_price_applied != null ? ` · min_applied: ${String(breakdown.min_price_applied)}` : ""}
                {breakdown?.setup_fee_used != null ? ` · setup_used: ${Number(breakdown.setup_fee_used).toFixed(2)}` : ""}
              </p>
            ) : null}
          </div>

          {detected3mfSettings && pricingReady && (
            <div className={`px-5 py-3 border-b bg-[#00FF9D]/[0.06] dark:bg-[#00FF9D]/[0.04] ${pub.borderSubtle}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span
                  className={`font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 border rounded-sm shrink-0 ${pub.borderStrongTone} text-[#008f5a] dark:text-[#00FF9D]`}
                >
                  3MF
                </span>
                <span className={`text-xs font-semibold font-sans ${pub.heroHeading}`}>
                  {userOverrode3mfSettings ? t("quote.3mf.updatedByUser") : t("quote.3mf.detectedTitle")}
                </span>
              </div>
              <p className={`text-[11px] leading-snug font-sans ${pub.muted}`}>
                {userOverrode3mfSettings ? t("quote.3mf.overrideSubtitle") : t("quote.3mf.detectedSubtitle")}
              </p>
              <dl className={`mt-2 space-y-0.5 font-mono text-[11px] tabular-nums ${pub.muted}`}>
                <div className="flex justify-between gap-3">
                  <dt>{t("quote.3mf.infill")}</dt>
                  <dd className={`text-right ${pub.heroHeading}`}>
                    {detected3mfSettings.infill_percent != null ? `%${detected3mfSettings.infill_percent}` : t("dash")}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>{t("quote.3mf.walls")}</dt>
                  <dd className={`text-right ${pub.heroHeading}`}>
                    {detected3mfSettings.wall_count != null ? String(detected3mfSettings.wall_count) : t("dash")}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>{t("quote.3mf.layer")}</dt>
                  <dd className={`text-right ${pub.heroHeading}`}>
                    {detected3mfSettings.layer_height_mm != null
                      ? `${Number(detected3mfSettings.layer_height_mm).toFixed(2)} mm`
                      : t("dash")}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>{t("quote.3mf.quality")}</dt>
                  <dd className={`text-right min-w-0 break-words ${pub.heroHeading}`}>
                    {detected3mfSettings.quality_profile != null && String(detected3mfSettings.quality_profile).trim() !== ""
                      ? String(detected3mfSettings.quality_profile)
                      : t("dash")}
                  </dd>
                </div>
              </dl>
              <p className={`text-[11px] leading-snug font-sans mt-2 ${pub.muted}`}>
                {userOverrode3mfSettings ? t("quote.3mf.overrideAccuracy") : t("quote.3mf.detectedAccuracy")}
              </p>
            </div>
          )}

          {pricing.high >= 1500 && (
            <div className={`px-5 py-3 border-b ${pub.borderSubtle} bg-amber-50/60 dark:bg-amber-500/5`}>
              <p className={`text-xs font-sans leading-relaxed ${pub.muted}`}>
                <span className="text-amber-600 dark:text-amber-400 font-semibold">{t("price.hint.label")}</span> {t("price.hint.body")}
              </p>
            </div>
          )}

          <div className="px-5 py-4 bg-neutral-50 dark:bg-[#080808]/80 space-y-2">
            <p className={`text-xs font-semibold text-center ${pub.heroHeading}`}>{t("price.question")}</p>
            <button
              type="button"
              data-testid="price-card-cta-scroll"
              onClick={() => {
                scrollToForm();
                trackQuoteCTAClick({ language });
              }}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4F00] hover:bg-[#E64700] text-black font-bold uppercase tracking-wider text-xs px-5 py-4 transition-all hover:-translate-y-0.5"
            >
              {t("price.cta")} <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <Collapsible open={calcOpen} onOpenChange={setCalcOpen} className={`border-b bg-white dark:bg-transparent ${pub.borderSubtle}`}>
            <CollapsibleTrigger
              data-testid="price-how-calculated-trigger"
              className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left font-sans text-sm font-medium transition-colors hover:bg-neutral-50/80 dark:hover:bg-white/[0.03] ${pub.heroHeading}`}
            >
              {t("price.collapsible.title")}
              <ChevronDown className={`w-5 h-5 shrink-0 text-[#FF4F00] transition-transform ${calcOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={`px-5 pb-5 space-y-3 text-sm font-sans ${pub.muted}`}>
                <DetailRow label={t("price.detail.infill")} value={printSettings?.infill_percent != null ? `%${printSettings.infill_percent}` : t("dash")} />
                <DetailRow label={t("price.detail.walls")} value={fmtPv(printSettings?.wall_count)} />
                <DetailRow
                  label={t("price.detail.layer")}
                  value={printSettings?.layer_height_mm != null ? `${Number(printSettings.layer_height_mm).toFixed(2)} mm` : t("dash")}
                />
                <DetailRow label={t("price.detail.material")} value={fmtPv(printSettings?.material)} />
                <DetailRow
                  label={t("price.detail.duration")}
                  value={
                    time ? `${Number(time.low).toFixed(1)} – ${Number(time.high).toFixed(1)} ${t("units.hours")}` : t("dash")
                  }
                />
                <DetailRow
                  label={t("price.detail.gram")}
                  value={mass ? `${Number(mass.low).toFixed(1)} – ${Number(mass.high).toFixed(1)} g` : t("dash")}
                />
                <DetailRow
                  label={t("price.detail.dimensions")}
                  value={
                    geometry?.bounding_box_mm
                      ? `${Number(geometry.bounding_box_mm.x).toFixed(1)} × ${Number(geometry.bounding_box_mm.y).toFixed(
                          1,
                        )} × ${Number(geometry.bounding_box_mm.z).toFixed(1)} mm`
                      : t("dash")
                  }
                />
                <DetailRow label={t("price.detail.triangles")} value={geometry?.triangle_count ? Number(geometry.triangle_count).toLocaleString(language === "tr" ? "tr-TR" : "en-US") : t("dash")} />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className={`px-5 py-4 border-b bg-white dark:bg-transparent ${pub.borderSubtle}`}>
            <div className={`flex items-start gap-2.5 text-sm leading-relaxed font-sans ${pub.muted}`}>
              <Info className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${pub.iconMuted}`} />
              <span>{t("price.disclaimer")}</span>
            </div>
          </div>

          <div className="px-5 py-5 bg-neutral-50 dark:bg-[#050505] space-y-4">
            <p className={`text-sm leading-relaxed font-sans ${pub.muted}`}>
              <span className="text-[#FF4F00] font-semibold">{t("price.sidebar.form")}</span> {t("price.sidebar.formBold")}
            </p>
            <div className={`pt-4 border-t ${pub.borderSubtle}`}>
              <p className={`text-sm leading-relaxed mb-3 font-sans ${pub.muted}`}>{t("price.sidebar.expertLead")}</p>
              <a
                href={site.whatsappLink(
                  geometry?.volume_cm3 && pricing?.low != null && pricing?.high != null
                    ? t("price.whatsapp.prefill", {
                        volume: Number(geometry.volume_cm3).toFixed(1),
                        low: formatTRY(pricing.low),
                        high: formatTRY(pricing.high),
                      })
                    : t("price.whatsapp.prefill.generic"),
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="price-whatsapp-cta"
                onClick={() => trackWhatsappClick("price-card", { language })}
                className="inline-flex items-center justify-between gap-3 w-full border border-emerald-500/40 hover:border-[#25D366] hover:bg-emerald-500/10 dark:border-[#25D366]/40 dark:hover:bg-[#25D366]/5 text-neutral-900 dark:text-white font-semibold uppercase tracking-wide text-xs px-4 py-3 transition-colors group font-sans"
              >
                <span className="flex items-center gap-2.5">
                  <MessageCircle className="w-4 h-4 text-emerald-600 dark:text-[#25D366]" strokeWidth={2} />
                  {t("price.sidebar.expertBtn")}
                </span>
                <ArrowUpRight className="w-3.5 h-3.5 text-neutral-500 dark:text-zinc-500 group-hover:text-[#25D366] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function fmtPrintLabel(v) {
  if (v === 0 || v === false) return String(v);
  if (v == null || v === "") return "—";
  return String(v);
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-neutral-100/90 dark:border-white/[0.06] last:border-0">
      <span className={`shrink-0 font-sans text-sm ${pub.muted}`}>{label}</span>
      <span className={`min-w-0 break-all break-words text-right tabular-nums font-mono text-sm ${pub.heroHeading}`}>{value}</span>
    </div>
  );
}
